/**
 * The locomotion graph read from the N1 manifest: which clip plays on which state edge, the loop
 * per state, the turn per state, the takeoff/descent clips and each action's frame count on the
 * loaded tier. Pure TypeScript (no Phaser); built once at boot and unit-tested against the
 * committed manifest so the controller never hard-codes what the manifest already says.
 */
import { N1_ACTIONS, type N1ActionId, type N1Manifest, type N1Tier } from '../assets/n1Manifest';
import { LOOP_ACTIONS, type LoopActionId } from './tuning';

export const LOCOMOTION_STATES = ['idle', 'walk', 'run', 'air'] as const;
export type LocomotionState = (typeof LOCOMOTION_STATES)[number];
export type GroundState = Exclude<LocomotionState, 'air'>;
export const GROUND_STATES: readonly GroundState[] = ['idle', 'walk', 'run'];

export interface ActionInfo {
  id: N1ActionId;
  kind: 'loop' | 'transition';
  from: LocomotionState;
  to: LocomotionState;
  facingChange: boolean;
  /** Frames on the loaded tier. */
  frames: number;
  /** First tier frame whose rootLift reaches the threshold (0 for grounded actions). */
  firstLiftedFrame: number;
}

const isState = (v: string): v is LocomotionState =>
  (LOCOMOTION_STATES as readonly string[]).includes(v);

export class ActionGraph {
  readonly actions: Record<N1ActionId, ActionInfo>;
  readonly loops: Record<GroundState, LoopActionId>;
  readonly turns: Partial<Record<GroundState, N1ActionId>>;
  /** run-to-jump: the only authored takeoff. */
  readonly takeoff: N1ActionId;
  private readonly edges = new Map<string, N1ActionId>();

  constructor(
    manifest: N1Manifest,
    readonly tier: N1Tier,
    liftThresholdPx: number,
  ) {
    const problems: string[] = [];
    const actions = {} as Record<N1ActionId, ActionInfo>;
    for (const id of N1_ACTIONS) {
      const a = manifest.actions[id];
      if (!isState(a.from) || !isState(a.to)) {
        problems.push(`${id}: states ${a.from}->${a.to} are not locomotion states`);
        continue;
      }
      const t = a.tiers[tier];
      const lift = t.rootLift;
      let firstLiftedFrame = 0;
      if (lift) {
        const i = lift.findIndex((v) => v >= liftThresholdPx);
        firstLiftedFrame = i < 0 ? 0 : Math.min(i, t.frames - 1);
      }
      actions[id] = {
        id,
        kind: a.kind,
        from: a.from,
        to: a.to,
        facingChange: a.facingChange,
        frames: t.frames,
        firstLiftedFrame,
      };
    }
    this.actions = actions;

    const loops: Partial<Record<GroundState, LoopActionId>> = {};
    const turns: Partial<Record<GroundState, N1ActionId>> = {};
    for (const id of N1_ACTIONS) {
      const info = actions[id];
      if (!info) continue;
      if (info.kind === 'loop') {
        if (info.from !== 'air' && (LOOP_ACTIONS as readonly string[]).includes(id))
          loops[info.from] = id as LoopActionId;
      } else if (info.facingChange) {
        if (info.from !== 'air') turns[info.from] = id;
      } else {
        this.edges.set(`${info.from}->${info.to}`, id);
      }
    }
    for (const s of GROUND_STATES) if (!loops[s]) problems.push(`no loop for state ${s}`);
    this.loops = loops as Record<GroundState, LoopActionId>;
    this.turns = turns;

    const takeoff = this.edges.get('run->air');
    if (!takeoff) problems.push('no run->air takeoff transition');
    this.takeoff = takeoff ?? 'run-to-jump';
    for (const to of ['run', 'idle'])
      if (!this.edges.get(`air->${to}`)) problems.push(`no air->${to} descent transition`);
    for (const s of GROUND_STATES) {
      for (const d of GROUND_STATES) {
        if (s === d) continue;
        try {
          this.path(s, d);
        } catch (e) {
          problems.push(e instanceof Error ? e.message : String(e));
        }
      }
    }
    if (problems.length)
      throw new Error(`N1 action graph incomplete:\n - ${problems.join('\n - ')}`);
  }

  /** The direct (non-turn) transition clip for an edge, if the manifest has one. */
  edge(from: LocomotionState, to: LocomotionState): N1ActionId | null {
    return this.edges.get(`${from}->${to}`) ?? null;
  }

  /** Descent clip for the landing state (jump-to-run when running, else jump-to-idle). */
  descent(to: 'run' | 'idle'): N1ActionId {
    return this.edges.get(`air->${to}`) ?? 'jump-to-idle';
  }

  /**
   * Shortest chain of ground transition clips from → to ([] when equal) — e.g. idle→run is
   * [idle-to-walk, walk-to-run] because the manifest has no idle-to-run.
   */
  path(from: GroundState, to: GroundState): N1ActionId[] {
    if (from === to) return [];
    const prev = new Map<GroundState, { state: GroundState; via: N1ActionId }>();
    const queue: GroundState[] = [from];
    const seen = new Set<GroundState>([from]);
    while (queue.length) {
      const s = queue.shift()!;
      for (const d of GROUND_STATES) {
        if (seen.has(d)) continue;
        const via = this.edges.get(`${s}->${d}`);
        if (!via) continue;
        seen.add(d);
        prev.set(d, { state: s, via });
        if (d === to) {
          const chain: N1ActionId[] = [];
          let cur: GroundState = to;
          while (cur !== from) {
            const p = prev.get(cur)!;
            chain.unshift(p.via);
            cur = p.state;
          }
          return chain;
        }
        queue.push(d);
      }
    }
    throw new Error(`no transition chain from ${from} to ${to}`);
  }
}

export function buildActionGraph(
  manifest: N1Manifest,
  tier: N1Tier,
  liftThresholdPx: number,
): ActionGraph {
  return new ActionGraph(manifest, tier, liftThresholdPx);
}
